<template>
  <div class="user-edit-container">
    <!-- LAYOUT 1: Split page for step 2 of registration process -->
    <div v-if="isFromRegister" class="auth-split-container animate-fade-in">
      <!-- Left Sidebar: Book Quote & Cozy Illustration (Desktop Only) -->
      <div class="auth-split-sidebar">
        <img src="@/assets/img/book_auth_bg.png" alt="Lối Đọc Library" class="auth-sidebar-img" />
        <div class="auth-sidebar-overlay auth-bg-gradient"></div>
        
        <div class="auth-sidebar-content px-4 text-white">
          <div class="d-flex align-items-center gap-3 mb-5">
            <div class="p-2 rounded-circle d-flex align-items-center justify-content-center" style="width: 50px; height: 50px; background-color: var(--bg-page) !important;">
              <icon icon="fa-solid fa-book-open" class="text-primary-color fs-3" />
            </div>
            <div>
              <h1 class="h2 fw-bold m-0 font-serif" style="letter-spacing: 0.5px;">Lối Đọc</h1>
              <small class="text-white-50">Nuôi dưỡng tâm hồn qua từng trang sách</small>
            </div>
          </div>

          <!-- Book Quote Slider with Glassmorphic Card -->
          <div class="glass-card p-4 text-white mt-5 position-relative overflow-hidden fade-in">
            <icon icon="fa-solid fa-quote-left" class="text-white-50 fs-2 mb-3" />
            <p class="fs-5 lh-base font-serif italic mb-3">
              "{{ currentQuote.text }}"
            </p>
            <div class="text-end text-white-50 fw-semibold font-serif">
              — {{ currentQuote.author }}
            </div>
            <!-- Decorative Book Icon floating in Card background -->
            <icon icon="fa-solid fa-book" class="position-absolute text-white-50 floating-book" style="bottom: -20px; right: -20px; font-size: 8rem; opacity: 0.08;" />
          </div>
        </div>
      </div>

      <!-- Right Side: Setup Form -->
      <div class="auth-split-main">
        <!-- App Header shown ONLY on Mobile -->
        <div class="mobile-app-header d-lg-none">
          <div class="mobile-app-brand font-serif">
            <icon icon="fa-solid fa-book-open" class="text-primary-color" />
            <span>Lối Đọc</span>
          </div>
        </div>

        <!-- Main Setup Card -->
        <div class="auth-form-card fade-in py-2">
          <!-- Dynamic Progress Bar -->
          <div v-if="!isProfileComplete" class="setup-progress mb-3 animate-fade-in">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <span class="fs-7 fw-bold text-primary-color">BƯỚC 2: HOÀN THIỆN HỒ SƠ CÁ NHÂN</span>
              <span class="badge bg-gold-badge px-2.5 py-1 text-xs text-white">{{ profileProgress }}% Hoàn tất</span>
            </div>
            <div class="progress-bar-container">
              <div class="progress-bar-fill" :style="{ width: profileProgress + '%' }"></div>
            </div>
          </div>

          <!-- Profile visual card -->
          <div class="profile-header-compact d-flex align-items-center gap-3 mb-4">
            <div class="avatar-glow-wrapper-compact">
              <div class="avatar-circle-compact">
                <icon icon="fa-solid fa-user-gear" class="avatar-icon-compact" />
              </div>
              <div class="glow-effect-compact"></div>
            </div>
            <div class="text-start">
              <h3 class="h5 fw-bold font-serif text-primary-color m-0">Chào mừng bạn!</h3>
              <p class="text-muted m-0 fs-7">Hoàn thiện hồ sơ cá nhân để sử dụng tính năng trao đổi sách cũ của chúng tôi</p>
            </div>
          </div>

          <!-- Edit Form -->
          <form @submit.prevent="handleUpdateAddress" novalidate>
            <!-- Phone Input -->
            <div class="custom-form-group">
              <label class="form-label font-serif">Số điện thoại</label>
              <div class="input-wrapper">
                <input
                  v-model.trim="tel"
                  type="tel"
                  class="custom-input"
                  :class="{ 'is-invalid border-danger': errors.tel }"
                  placeholder="Số điện thoại (VD: 0912345678)"
                  @input="clearError('tel')"
                  @blur="validateTel"
                />
                <icon icon="fa-solid fa-phone" class="custom-input-icon" />
              </div>
              <div v-if="errors.tel" class="error-message">
                {{ errors.tel }}
              </div>
            </div>

            <!-- Province Select -->
            <div class="custom-form-group">
              <label class="form-label font-serif">Tỉnh / Thành phố</label>
              <div class="input-wrapper custom-dropdown-container">
                <div 
                  @click="toggleProvinceDropdown($event)" 
                  class="custom-input custom-dropdown-trigger"
                  :class="{ 'placeholder-color': !provinceCode, 'is-invalid border-danger': errors.provinceCode }"
                >
                  <span>{{ selectedProvinceName || 'Tỉnh / Thành phố' }}</span>
                  <icon icon="fa-solid fa-chevron-down" class="dropdown-chevron-icon" />
                </div>
                <icon icon="fa-solid fa-city" class="custom-input-icon" />
                
                <!-- Custom Options Menu -->
                <div v-if="showProvinceMenu" class="custom-dropdown-menu">
                  <div class="dropdown-search-wrapper" @click.stop>
                    <div class="position-relative">
                      <input 
                        v-model="provinceSearchQuery" 
                        ref="provinceSearchInputReg"
                        type="text" 
                        class="dropdown-search-input" 
                        placeholder="Tìm nhanh tỉnh/thành..." 
                      />
                      <icon icon="fa-solid fa-magnifying-glass" class="dropdown-search-icon" />
                    </div>
                  </div>
                  <div class="dropdown-items-scroller">
                    <div 
                      v-for="province in filteredProvinces" 
                      :key="province.code" 
                      @click="selectProvince(province)"
                      class="custom-dropdown-item"
                      :class="{ 'active': provinceCode === province.code }"
                    >
                      {{ province.name }}
                    </div>
                    <div v-if="filteredProvinces.length === 0" class="custom-dropdown-item text-muted text-center py-3">
                      Không tìm thấy kết quả
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="errors.provinceCode" class="error-message">
                {{ errors.provinceCode }}
              </div>
            </div>

            <!-- Ward Select -->
            <div class="custom-form-group">
              <label class="form-label font-serif">Xã / Phường / Thị trấn</label>
              <div class="input-wrapper custom-dropdown-container">
                <div 
                  @click="provinceCode && toggleWardDropdown($event)" 
                  class="custom-input custom-dropdown-trigger"
                  :class="{ 'placeholder-color': !wardCode, 'disabled-trigger': !provinceCode, 'is-invalid border-danger': errors.wardCode }"
                >
                  <span>{{ selectedWardName || 'Xã / Phường / Thị trấn' }}</span>
                  <icon icon="fa-solid fa-chevron-down" class="dropdown-chevron-icon" />
                </div>
                <icon icon="fa-solid fa-building" class="custom-input-icon" />
                
                <!-- Custom Options Menu -->
                <div v-if="showWardMenu && provinceCode" class="custom-dropdown-menu">
                  <div class="dropdown-search-wrapper" @click.stop>
                    <div class="position-relative">
                      <input 
                        v-model="wardSearchQuery" 
                        ref="wardSearchInputReg"
                        type="text" 
                        class="dropdown-search-input" 
                        placeholder="Tìm nhanh xã/phường..." 
                      />
                      <icon icon="fa-solid fa-magnifying-glass" class="dropdown-search-icon" />
                    </div>
                  </div>
                  <div class="dropdown-items-scroller">
                    <div 
                      v-for="ward in filteredWards" 
                      :key="ward.code" 
                      @click="selectWard(ward)"
                      class="custom-dropdown-item"
                      :class="{ 'active': wardCode === ward.code }"
                    >
                      {{ ward.name }}
                    </div>
                    <div v-if="filteredWards.length === 0" class="custom-dropdown-item text-muted text-center py-3">
                      Không tìm thấy kết quả
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="errors.wardCode" class="error-message">
                {{ errors.wardCode }}
              </div>
            </div>

            <!-- Address Input -->
            <div class="custom-form-group">
              <label class="form-label font-serif">Địa chỉ chi tiết (Số nhà, tên đường...)</label>
              <div class="input-wrapper textarea-wrapper">
                <textarea
                  v-model.trim="address"
                  class="custom-input custom-textarea"
                  :class="{ 'is-invalid border-danger': errors.address }"
                  placeholder="Địa chỉ chi tiết (Số nhà, tên đường...)"
                  rows="2"
                  @input="clearError('address')"
                ></textarea>
                <icon icon="fa-solid fa-house-user" class="custom-input-icon textarea-icon" />
              </div>
              <div v-if="errors.address" class="error-message">
                {{ errors.address }}
              </div>
            </div>

            <!-- Instructions Input -->
            <div class="custom-form-group">
              <label class="form-label font-serif">Hướng dẫn chỉ đường (Tùy chọn)</label>
              <div class="input-wrapper textarea-wrapper">
                <textarea
                  v-model.trim="instructions"
                  class="custom-input custom-textarea"
                  :class="{ 'is-invalid border-danger': errors.instructions }"
                  placeholder="Hướng dẫn chỉ đường (Tùy chọn)"
                  rows="2"
                  @input="clearError('instructions')"
                ></textarea>
                <icon icon="fa-solid fa-compass" class="custom-input-icon textarea-icon" />
              </div>
              <div v-if="errors.instructions" class="error-message">
                {{ errors.instructions }}
              </div>
            </div>

            <!-- Submit Button -->
            <button 
              type="submit" 
              class="btn-book-primary mt-2" 
              :disabled="isLoadingAddress"
            >
              <span v-if="isLoadingAddress" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <icon v-else icon="fa-solid fa-arrow-right" />
              <span>{{ isLoadingAddress ? 'Đang cập nhật...' : (isFromRegister ? 'Tiếp tục thiết lập sở thích' : 'Lưu thông tin') }}</span>
            </button>
            
            <!-- Skip Button -->
            <div class="text-center mt-3">
              <button 
                type="button" 
                class="btn btn-link fs-7 text-decoration-none text-muted p-0" 
                @click="skipUpdate"
                :disabled="isLoadingAddress"
              >
                Bỏ qua bước này
              </button>
            </div>
          </form>
        </div>

        <!-- Footer Space -->
        <div class="mt-3 text-center">
          <span class="fs-8 text-muted">Lối Đọc Library © 2026. All rights reserved.</span>
        </div>
      </div>
    </div>

    <!-- LAYOUT 2: Renders ONLY the form when embedded inside SettingsLayout -->
    <div v-else class="profile-form-inner-content animate-fade-in text-start">
      <!-- Dynamic Progress Bar -->
      <div v-if="!isProfileComplete" class="setup-progress mb-4 animate-fade-in">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="fs-7 fw-bold text-primary-color">BƯỚC HOÀN THIỆN HỒ SƠ CÁ NHÂN</span>
          <span class="badge bg-gold-badge px-2.5 py-1 text-xs text-white">{{ profileProgress }}% Hoàn tất</span>
        </div>
        <div class="progress-bar-container">
          <div class="progress-bar-fill" :style="{ width: profileProgress + '%' }"></div>
        </div>
      </div>

      <h4 class="h5 fw-bold font-serif text-primary-color mb-4 pb-2 border-bottom">
        <icon icon="fa-solid fa-user" class="me-2" />
        Thông tin tài khoản
      </h4>

      <form @submit.prevent="handleUpdateAccount" novalidate class="mb-5">
        <div class="row g-3">
          <!-- Fullname Input -->
          <div class="col-md-12">
            <div class="custom-form-group">
              <label class="form-label font-serif">Họ và tên</label>
              <div class="input-wrapper">
                <input
                  v-model.trim="fullname"
                  type="text"
                  class="custom-input"
                  :class="{ 'is-invalid border-danger': errors.fullname }"
                  placeholder="Họ và tên"
                  @input="clearError('fullname')"
                />
                <icon icon="fa-solid fa-user" class="custom-input-icon" />
              </div>
              <div v-if="errors.fullname" class="error-message">
                {{ errors.fullname }}
              </div>
            </div>
          </div>

          <!-- Username Input -->
          <div class="col-md-6">
            <div class="custom-form-group">
              <label class="form-label font-serif">Tên đăng nhập</label>
              <div class="input-wrapper">
                <input
                  v-model.trim="username"
                  type="text"
                  class="custom-input"
                  :class="{ 'is-invalid border-danger': errors.username, 'bg-light text-muted': !canEditUsername }"
                  placeholder="Tên đăng nhập"
                  :disabled="!canEditUsername"
                  @input="clearError('username')"
                />
                <icon icon="fa-solid fa-id-card" class="custom-input-icon" />
              </div>
              <div v-if="errors.username" class="error-message">
                {{ errors.username }}
              </div>
            </div>
          </div>

          <!-- YOB Input -->
          <div class="col-md-6">
            <div class="custom-form-group">
              <label class="form-label font-serif">Năm sinh</label>
              <div class="input-wrapper">
                <input
                  v-model.number="yob"
                  type="number"
                  class="custom-input"
                  :class="{ 'is-invalid border-danger': errors.yob }"
                  placeholder="Năm sinh (VD: 2000)"
                  @input="clearError('yob')"
                />
                <icon icon="fa-solid fa-calendar" class="custom-input-icon" />
              </div>
              <div v-if="errors.yob" class="error-message">
                {{ errors.yob }}
              </div>
            </div>
          </div>

          <!-- Password Input -->
          <div class="col-md-6">
            <div class="custom-form-group">
              <label class="form-label font-serif">
                Mật khẩu mới <span v-if="!canEditUsername" class="fw-normal text-muted fs-8">(Bỏ trống nếu không đổi)</span>
              </label>
              <div class="input-wrapper">
                <input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="custom-input"
                  :class="{ 'is-invalid border-danger': errors.password }"
                  placeholder="Mật khẩu mới"
                  @input="clearError('password')"
                />
                <icon icon="fa-solid fa-lock" class="custom-input-icon" />
                <icon 
                  :icon="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" 
                  class="custom-input-eye"
                  @click="togglePassword"
                />
              </div>
              <div v-if="errors.password" class="error-message">
                {{ errors.password }}
              </div>
            </div>
          </div>

          <!-- Confirm Password Input -->
          <div class="col-md-6">
            <div class="custom-form-group">
              <label class="form-label font-serif">Xác nhận mật khẩu mới</label>
              <div class="input-wrapper">
                <input
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="custom-input"
                  :class="{ 'is-invalid border-danger': errors.confirmPassword }"
                  placeholder="Xác nhận mật khẩu mới"
                  @input="clearError('confirmPassword')"
                />
                <icon icon="fa-solid fa-lock" class="custom-input-icon" />
                <icon 
                  :icon="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" 
                  class="custom-input-eye"
                  @click="toggleConfirmPassword"
                />
              </div>
              <div v-if="errors.confirmPassword" class="error-message">
                {{ errors.confirmPassword }}
              </div>
            </div>
          </div>

          <!-- Button Cập nhật tài khoản -->
          <div class="col-12 mt-4 text-end">
            <button 
              type="submit" 
              class="btn-book-primary px-4 py-2.5 d-inline-flex align-items-center gap-2" 
              :disabled="isLoadingAccount"
              style="width: auto;"
            >
              <span v-if="isLoadingAccount" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <icon v-else icon="fa-solid fa-save" class="me-2" />
              <span>Cập nhật tài khoản</span>
            </button>
          </div>
        </div>
      </form>

      <h4 class="h5 fw-bold font-serif text-primary-color mb-4 pb-2 border-bottom">
        <icon icon="fa-solid fa-address-book" class="me-2" />
        Thông tin địa chỉ
      </h4>

      <form @submit.prevent="handleUpdateAddress" novalidate>
        <div class="row g-3">
          <!-- Phone Input -->
          <div class="col-md-12">
            <div class="custom-form-group">
              <label class="form-label font-serif">Số điện thoại</label>
              <div class="input-wrapper">
                <input
                  v-model.trim="tel"
                  type="tel"
                  class="custom-input"
                  :class="{ 'is-invalid border-danger': errors.tel }"
                  placeholder="Nhập số điện thoại liên hệ (VD: 0912345678)"
                  @input="clearError('tel')"
                  @blur="validateTel"
                />
                <icon icon="fa-solid fa-phone" class="custom-input-icon" />
              </div>
              <div v-if="errors.tel" class="error-message">
                {{ errors.tel }}
              </div>
            </div>
          </div>

          <!-- Province Select -->
          <div class="col-md-6">
            <div class="custom-form-group">
              <label class="form-label font-serif">Tỉnh / Thành phố</label>
              <div class="input-wrapper custom-dropdown-container">
                <div 
                  @click="toggleProvinceDropdown($event)" 
                  class="custom-input custom-dropdown-trigger"
                  :class="{ 'placeholder-color': !provinceCode, 'is-invalid border-danger': errors.provinceCode }"
                >
                  <span>{{ selectedProvinceName || 'Chọn Tỉnh / Thành phố' }}</span>
                  <icon icon="fa-solid fa-chevron-down" class="dropdown-chevron-icon" />
                </div>
                <icon icon="fa-solid fa-city" class="custom-input-icon" />
                
                <!-- Custom Options Menu -->
                <div v-if="showProvinceMenu" class="custom-dropdown-menu">
                  <div class="dropdown-search-wrapper" @click.stop>
                    <div class="position-relative">
                      <input 
                        v-model="provinceSearchQuery" 
                        ref="provinceSearchInputEdit"
                        type="text" 
                        class="dropdown-search-input" 
                        placeholder="Tìm nhanh tỉnh/thành..." 
                      />
                      <icon icon="fa-solid fa-magnifying-glass" class="dropdown-search-icon" />
                    </div>
                  </div>
                  <div class="dropdown-items-scroller">
                    <div 
                      v-for="province in filteredProvinces" 
                      :key="province.code" 
                      @click="selectProvince(province)"
                      class="custom-dropdown-item"
                      :class="{ 'active': provinceCode === province.code }"
                    >
                      {{ province.name }}
                    </div>
                    <div v-if="filteredProvinces.length === 0" class="custom-dropdown-item text-muted text-center py-3">
                      Không tìm thấy kết quả
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="errors.provinceCode" class="error-message">
                {{ errors.provinceCode }}
              </div>
            </div>
          </div>

          <!-- Ward Select -->
          <div class="col-md-6">
            <div class="custom-form-group">
              <label class="form-label font-serif">Xã / Phường / Thị trấn</label>
              <div class="input-wrapper custom-dropdown-container">
                <div 
                  @click="provinceCode && toggleWardDropdown($event)" 
                  class="custom-input custom-dropdown-trigger"
                  :class="{ 'placeholder-color': !wardCode, 'disabled-trigger': !provinceCode, 'is-invalid border-danger': errors.wardCode }"
                >
                  <span>{{ selectedWardName || 'Chọn Xã / Phường / Thị trấn' }}</span>
                  <icon icon="fa-solid fa-chevron-down" class="dropdown-chevron-icon" />
                </div>
                <icon icon="fa-solid fa-building" class="custom-input-icon" />
                
                <!-- Custom Options Menu -->
                <div v-if="showWardMenu && provinceCode" class="custom-dropdown-menu">
                  <div class="dropdown-search-wrapper" @click.stop>
                    <div class="position-relative">
                      <input 
                        v-model="wardSearchQuery" 
                        ref="wardSearchInputEdit"
                        type="text" 
                        class="dropdown-search-input" 
                        placeholder="Tìm nhanh xã/phường..." 
                      />
                      <icon icon="fa-solid fa-magnifying-glass" class="dropdown-search-icon" />
                    </div>
                  </div>
                  <div class="dropdown-items-scroller">
                    <div 
                      v-for="ward in filteredWards" 
                      :key="ward.code" 
                      @click="selectWard(ward)"
                      class="custom-dropdown-item"
                      :class="{ 'active': wardCode === ward.code }"
                    >
                      {{ ward.name }}
                    </div>
                    <div v-if="filteredWards.length === 0" class="custom-dropdown-item text-muted text-center py-3">
                      Không tìm thấy kết quả
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="errors.wardCode" class="error-message">
                {{ errors.wardCode }}
              </div>
            </div>
          </div>

          <!-- Address Input -->
          <div class="col-md-12">
            <div class="custom-form-group">
              <label class="form-label font-serif">Địa chỉ chi tiết (Số nhà, tên đường...)</label>
              <div class="input-wrapper textarea-wrapper">
                <textarea
                  v-model.trim="address"
                  class="custom-input custom-textarea"
                  :class="{ 'is-invalid border-danger': errors.address }"
                  placeholder="Ví dụ: Số 123, đường Nguyễn Trãi"
                  rows="2"
                  @input="clearError('address')"
                ></textarea>
                <icon icon="fa-solid fa-house-user" class="custom-input-icon textarea-icon" />
              </div>
              <div v-if="errors.address" class="error-message">
                {{ errors.address }}
              </div>
            </div>
          </div>

          <!-- Instructions Input -->
          <div class="col-md-12">
            <div class="custom-form-group">
              <label class="form-label font-serif">Hướng dẫn chỉ đường (Tùy chọn)</label>
              <div class="input-wrapper textarea-wrapper">
                <textarea
                  v-model.trim="instructions"
                  class="custom-input custom-textarea"
                  :class="{ 'is-invalid border-danger': errors.instructions }"
                  placeholder="Ví dụ: Nhà màu xanh cạnh tiệm thuốc lá"
                  rows="2"
                  @input="clearError('instructions')"
                ></textarea>
                <icon icon="fa-solid fa-compass" class="custom-input-icon textarea-icon" />
              </div>
              <div v-if="errors.instructions" class="error-message">
                {{ errors.instructions }}
              </div>
            </div>
          </div>
        </div>

        <!-- Save Button -->
        <div class="mt-4 pt-3 border-top d-flex justify-content-end">
          <button 
            type="submit" 
            class="btn-book-primary px-4 py-2.5 d-inline-flex align-items-center gap-2" 
            :disabled="isLoadingAddress"
            style="width: auto;"
          >
            <span v-if="isLoadingAddress" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <icon v-else icon="fa-solid fa-floppy-disk" />
            <span>{{ isLoadingAddress ? 'Đang lưu...' : 'Lưu thông tin địa chỉ' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script src="./user-edit.js"></script>
<style scoped src="./user-edit.css"></style>
